import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import chevronTeal from "@assets/facilityNetwork/req-chevron-up.svg";
import {
  acceptIncomingChildLinkRequest,
  cancelChildLinkRequest,
  confirmChildLinkRequests,
  declineIncomingChildLinkRequest,
  listChildLinkRequests,
  listIncomingChildLinkRequests,
  listLinkedFacilities,
  resendChildLinkRequest,
} from "@services/facilityNetworkLink";
import {
  approveUnlinkRequest,
  cancelUnlinkRequest,
  listUnlinkRequests,
  rejectUnlinkRequest,
  resendUnlinkRequest,
} from "@services/facilityNetworkUnlink";
import {
  clearFacilityNetworkListCache,
  isFacilityNetworkNotFoundMessage,
} from "@services/facilityNetworkListFetch";
import {
  consumeNetworkRequestFocus,
  NETWORK_REQUEST_FOCUS_EVENT,
  NETWORK_REQUESTS_REFRESH_EVENT,
  peekNetworkRequestFocus,
  type NetworkRequestFocus,
} from "@services/notifications";
import { npiLookupService } from "@services/npiLookup";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { mapNpiResultToSearchHit } from "./addChildFacilitySearchUtils";
import FacilityNetworkFigmaHeading from "./FacilityNetworkFigmaHeading";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import FacilityNetworkRequestDetail from "./FacilityNetworkRequestDetail";
import {
  applyNpiHitsToInboxItems,
  buildNetworkInboxItems,
  compareResolvedNewestFirst,
  inboxItemNeedsRegistryEnrichment,
  matchInboxItemForNotification,
  type InboxPill,
  type NetworkInboxItem,
} from "./facilityNetworkInboxUtils";
import { unlinkAcceptedSuccessMessage } from "./facilityNetworkUnlinkRequestUtils";
import type { LinkedFacilityNpiHit } from "./facilityNetworkChildrenUtils";
import { FN } from "./facilityNetworkTheme";

export type { NetworkInboxItem };

type Props = {
  parentFacilityId?: number | null;
  selectedFacilityId?: number | null;
  includeIncoming?: boolean;
  showHeading?: boolean;
  onNetworkMutated?: () => void;
  onOpenCountChange?: (count: number) => void;
};

async function fillMissingInviteProfileFromNpi(
  items: NetworkInboxItem[],
): Promise<NetworkInboxItem[]> {
  const missing = items.filter(inboxItemNeedsRegistryEnrichment);
  if (missing.length === 0) return items;
  const countryCode = getSelectedCountryCode();
  const hitsByNpi: Record<string, LinkedFacilityNpiHit> = {};
  await Promise.all(
    missing.map(async (item) => {
      const npi = (item.npi || "").replace(/\D/g, "").slice(-10);
      if (npi.length < 10) return;
      try {
        const response = await npiLookupService.lookupNPI({
          npi_number: npi,
          country_code: countryCode,
        });
        const result = response.data?.results?.[0];
        if (!result) return;
        const hit = mapNpiResultToSearchHit(result);
        if (!hit) return;
        hitsByNpi[npi] = {
          city: hit.city,
          state: hit.state,
          ...(hit.name ? { name: hit.name } : {}),
          ...(hit.facilityType ? { facilityType: hit.facilityType } : {}),
          ...(hit.ccn ? { ccn: hit.ccn } : {}),
        };
      } catch {
        return;
      }
    }),
  );
  return applyNpiHitsToInboxItems(items, hitsByNpi);
}

function StatusPill({
  pill,
  outcome,
  status,
}: {
  pill: InboxPill;
  outcome?: NetworkInboxItem["outcomePill"];
  status?: string;
}) {
  const isInviteNeeded = pill === "invite" && status === "action_needed";
  const isInviteSent = pill === "invite" && status === "invite_sent";
  const isLink = pill === "link";
  const bgcolor = isInviteNeeded || isInviteSent ? "#fff4e5" : isLink ? FN.metricCardBg : FN.errorBg;
  const border = isInviteNeeded || isInviteSent ? "#fabc89" : isLink ? FN.panelBorder : FN.errorBorder;
  const color = isInviteNeeded || isInviteSent ? FN.figmaWarning : isLink ? FN.primary : FN.figmaError;
  const label = isInviteSent ? "Invite sent" : isInviteNeeded ? "Invite needed" : isLink ? "Link request" : "Unlink request";

  const outcomeStyles =
    outcome === "approved" || outcome === "accepted"
      ? { bgcolor: FN.metricCardBg, border: FN.panelBorder, color: FN.primary, label: outcome === "accepted" ? "Accepted" : "Approved" }
      : outcome === "rejected" || outcome === "declined" || outcome === "cancelled"
        ? {
            bgcolor: FN.errorBg,
            border: FN.errorBorder,
            color: FN.figmaError,
            label: outcome === "declined" ? "Declined" : outcome === "cancelled" ? "Cancelled" : "Rejected",
          }
        : null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
      <Box
        sx={{
          bgcolor,
          border: `1px solid ${border}`,
          borderRadius: "68px",
          px: "9px",
          py: "3px",
        }}
      >
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 10,
            color,
            lineHeight: "normal",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
      </Box>
      {outcomeStyles ? (
        <Box
          sx={{
            bgcolor: outcomeStyles.bgcolor,
            border: `1px solid ${outcomeStyles.border}`,
            borderRadius: "68px",
            px: "9px",
            py: "3px",
          }}
        >
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 10,
              color: outcomeStyles.color,
              lineHeight: "normal",
              whiteSpace: "nowrap",
            }}
          >
            {outcomeStyles.label}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

function BucketPanel({
  title,
  count,
  items,
  selectedKey,
  onSelect,
}: {
  title: string;
  count: number;
  items: NetworkInboxItem[];
  selectedKey: string | null;
  onSelect: (item: NetworkInboxItem) => void;
}) {
  return (
    <Box
      sx={{
        border: `1px solid ${FN.figmaCardBorder}`,
        borderRadius: FN.figmaCardRadius,
        minHeight: 200,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          borderBottom: `1px solid ${FN.figmaCardBorder}`,
        }}
      >
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 16,
            fontWeight: 600,
            color: FN.title,
            lineHeight: "22px",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            bgcolor: FN.figmaShareIconBg,
            borderRadius: "24px",
            minWidth: 24,
            px: 1.5,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: 12, color: FN.title, lineHeight: "16px" }}>{count}</Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {items.map((item) => {
          const selected = item.key === selectedKey;
          return (
            <Box
              key={item.key}
              component="button"
              type="button"
              onClick={() => onSelect(item)}
              sx={{
                display: "flex",
                width: "100%",
                textAlign: "left",
                border: "none",
                borderBottom: `1px solid ${FN.figmaCardBorder}`,
                bgcolor: selected ? "#f5f5f5" : "#fff",
                p: "12px",
                cursor: "pointer",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    fontWeight: 600,
                    color: FN.figmaNeutral,
                    letterSpacing: "0.25px",
                    lineHeight: "16px",
                  }}
                >
                  {item.facilityName}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                  <StatusPill
                    pill={item.pill}
                    outcome={item.bucket === "resolved" ? item.outcomePill : null}
                    status={item.status}
                  />
                  {item.metaLabel ? (
                    <>
                      <Typography sx={{ fontSize: 12, color: FN.figmaAccent }}>·</Typography>
                      <Typography sx={{ fontSize: 12, color: FN.figmaAccent, letterSpacing: "0.25px" }}>
                        {item.metaLabel}
                      </Typography>
                    </>
                  ) : null}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function FacilityNetworkRequestsInbox({
  parentFacilityId,
  selectedFacilityId,
  includeIncoming = true,
  showHeading = true,
  onNetworkMutated,
  onOpenCountChange,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<NetworkInboxItem[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingFocus, setPendingFocus] = useState<{
    focus: NetworkRequestFocus;
    gen: number;
  } | null>(null);
  const [settledGen, setSettledGen] = useState(0);
  const quietReloadRef = useRef(false);
  const focusGenRef = useRef(0);
  const hasItemsRef = useRef(false);
  hasItemsRef.current = items.length > 0;

  const reload = useCallback((opts?: { quiet?: boolean }) => {
    quietReloadRef.current = Boolean(opts?.quiet);
    clearFacilityNetworkListCache();
    setReloadToken((value) => value + 1);
    onNetworkMutated?.();
  }, [onNetworkMutated]);

  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    const applyStoredFocus = () => {
      const next = peekNetworkRequestFocus();
      if (!next) return;
      focusGenRef.current += 1;
      setPendingFocus({ focus: next, gen: focusGenRef.current });
      reloadRef.current();
    };
    const onRefresh = () => {
      reloadRef.current({ quiet: true });
    };
    applyStoredFocus();
    window.addEventListener(NETWORK_REQUEST_FOCUS_EVENT, applyStoredFocus);
    window.addEventListener(NETWORK_REQUESTS_REFRESH_EVENT, onRefresh);
    return () => {
      window.removeEventListener(NETWORK_REQUEST_FOCUS_EVENT, applyStoredFocus);
      window.removeEventListener(NETWORK_REQUESTS_REFRESH_EVENT, onRefresh);
    };
  }, []);

  useEffect(() => {
    if (!parentFacilityId && !selectedFacilityId) {
      setItems([]);
      onOpenCountChange?.(0);
      return;
    }
    let cancelled = false;
    const fetchGen = focusGenRef.current;
    const quiet = quietReloadRef.current;
    quietReloadRef.current = false;
    if (!quiet || !hasItemsRef.current) setLoading(true);
    setError(null);
    const sentPromise = parentFacilityId
      ? listChildLinkRequests(parentFacilityId)
      : Promise.resolve([]);
    const receivedPromise =
      includeIncoming && selectedFacilityId
        ? listIncomingChildLinkRequests(selectedFacilityId)
        : Promise.resolve([]);
    const initiatedUnlinkPromise = selectedFacilityId
      ? listUnlinkRequests(selectedFacilityId, "initiated")
      : Promise.resolve([]);
    const receivedUnlinkPromise = selectedFacilityId
      ? listUnlinkRequests(selectedFacilityId, "received")
      : Promise.resolve([]);
    const linkedPromise = parentFacilityId
      ? listLinkedFacilities(parentFacilityId)
      : Promise.resolve([]);

    Promise.allSettled([
      sentPromise,
      receivedPromise,
      initiatedUnlinkPromise,
      receivedUnlinkPromise,
      linkedPromise,
    ])
      .then(async (results) => {
        if (cancelled) return;
        const readList = <T,>(result: PromiseSettledResult<T[]>): T[] => {
          if (result.status === "fulfilled") {
            return Array.isArray(result.value) ? result.value : [];
          }
          const message =
            result.reason instanceof Error ? result.reason.message : "Failed to load requests.";
          if (!isFacilityNetworkNotFoundMessage(message)) {
            setError(message);
          }
          return [];
        };
        const [sentResult, receivedResult, initiatedUnlinkResult, receivedUnlinkResult, linkedResult] =
          results;
        const nextItems = buildNetworkInboxItems({
          sent: readList(sentResult),
          received: readList(receivedResult),
          initiatedUnlink: readList(initiatedUnlinkResult),
          receivedUnlink: readList(receivedUnlinkResult),
          linked: readList(linkedResult),
          parentFacilityId,
          selectedFacilityId,
        });
        const enriched = await fillMissingInviteProfileFromNpi(nextItems);
        if (cancelled) return;
        setItems(enriched);
        onOpenCountChange?.(
          enriched.filter((item) => item.bucket === "needs" || item.bucket === "awaiting").length,
        );
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setSettledGen(fetchGen);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [parentFacilityId, selectedFacilityId, includeIncoming, reloadToken, onOpenCountChange]);

  const needs = useMemo(() => items.filter((item) => item.bucket === "needs"), [items]);
  const awaiting = useMemo(() => items.filter((item) => item.bucket === "awaiting"), [items]);
  const resolved = useMemo(
    () =>
      items
        .filter((item) => item.bucket === "resolved")
        .sort(compareResolvedNewestFirst),
    [items],
  );
  const selected = items.find((item) => item.key === selectedKey) ?? null;

  useEffect(() => {
    if (selectedKey && !items.some((item) => item.key === selectedKey)) {
      setSelectedKey(null);
    }
  }, [items, selectedKey]);

  useEffect(() => {
    if (!pendingFocus || loading || settledGen < pendingFocus.gen) return;
    const match = matchInboxItemForNotification(items, pendingFocus.focus);
    if (match) {
      if (match.bucket === "resolved") setShowResolved(true);
      setActionError(null);
      setSelectedKey(match.key);
    }
    consumeNetworkRequestFocus();
    setPendingFocus(null);
  }, [pendingFocus, items, loading, settledGen]);

  const runAction = async (work: () => Promise<unknown>, successMessage: string) => {
    setSubmitting(true);
    setActionError(null);
    try {
      await work();
      toast.success(successMessage);
      setSelectedKey(null);
      reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Request failed.";
      setActionError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeclineUnlink = (reason: string) => {
    if (!selected || selected.kind !== "unlink") return;
    const note = reason.trim();
    if (!note) return;
    void runAction(
      () => rejectUnlinkRequest(selected.actionFacilityId, selected.requestId, note),
      "Unlink request disapproved.",
    );
  };

  const handleAcceptUnlink = () => {
    if (!selected || selected.kind !== "unlink") return;
    void runAction(
      () => approveUnlinkRequest(selected.actionFacilityId, selected.requestId),
      unlinkAcceptedSuccessMessage({
        initiatorRole: selected.initiatorRole,
        counterpartyName: selected.facilityName,
      }),
    );
  };

  const handleDeclineLink = (reason: string) => {
    if (!selected || selected.kind !== "link") return;
    void runAction(
      () => declineIncomingChildLinkRequest(selected.actionFacilityId, selected.requestId, reason),
      "Link request declined.",
    );
  };

  const handleAcceptLink = () => {
    if (!selected || selected.kind !== "link") return;
    const successMessage = selected.coordinationMode
      ? selected.currentParentFacilityName
        ? `Unlink request sent to ${selected.currentParentFacilityName}.`
        : "Unlink request sent to your current parent."
      : "Link request accepted.";
    void runAction(
      () => acceptIncomingChildLinkRequest(selected.actionFacilityId, selected.requestId),
      successMessage,
    );
  };

  const handleCancelOutgoing = () => {
    if (!selected) return;
    if (selected.kind === "unlink") {
      void runAction(
        () => cancelUnlinkRequest(selected.actionFacilityId, selected.requestId),
        "Unlink request cancelled.",
      );
      return;
    }
    void runAction(
      () => cancelChildLinkRequest(selected.actionFacilityId, selected.requestId),
      "Link request cancelled.",
    );
  };

  const handleResend = () => {
    if (!selected) return;
    if (selected.kind === "unlink") {
      void runAction(
        () => resendUnlinkRequest(selected.actionFacilityId, selected.requestId),
        "Unlink request resent.",
      );
      return;
    }
    void runAction(
      () => resendChildLinkRequest(selected.actionFacilityId, selected.requestId),
      "Link request resent.",
    );
  };

  const handleSendInvite = (email: string) => {
    const targetId = (selected?.npi || selected?.ccn || "").trim();
    if (!selected || !parentFacilityId || !targetId) return;
    void runAction(
      () =>
        confirmChildLinkRequests(
          parentFacilityId,
          [],
          [targetId],
          { [targetId]: email.trim() },
        ),
      "Invite sent to facility.",
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minWidth: 0,
        width: "100%",
        flex: 1,
      }}
    >
      {showHeading ? (
        <FacilityNetworkFigmaHeading
          title="Requests"
          subtitle="Pending link and unlink requests across your network"
        />
      ) : null}

      {loading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} sx={{ color: FN.primary }} />
        </Box>
      ) : error ? (
        <Typography sx={{ fontSize: 14, color: FN.errorText }}>{error}</Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "408px 1fr" },
            gridTemplateRows: { md: "auto auto" },
            gap: 1.5,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              minWidth: 0,
              minHeight: 0,
              gridColumn: { md: "1" },
              gridRow: { md: "1" },
            }}
          >
            <BucketPanel
              title="Needs your response"
              count={needs.length}
              items={needs}
              selectedKey={selectedKey}
              onSelect={(item) => {
                setActionError(null);
                setSelectedKey(item.key);
              }}
            />
            <BucketPanel
              title="Awaiting response"
              count={awaiting.length}
              items={awaiting}
              selectedKey={selectedKey}
              onSelect={(item) => {
                setActionError(null);
                setSelectedKey(item.key);
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              minHeight: 0,
              height: "100%",
              gridColumn: { md: "2" },
              gridRow: { md: "1" },
            }}
          >
            <FacilityNetworkRequestDetail
              selected={selected}
              needsCount={needs.length}
              awaitingCount={awaiting.length}
              submitting={submitting}
              actionError={actionError}
              onClose={() => {
                setActionError(null);
                setSelectedKey(null);
              }}
              onDeclineUnlink={handleDeclineUnlink}
              onAcceptUnlink={handleAcceptUnlink}
              onDeclineLink={handleDeclineLink}
              onAcceptLink={handleAcceptLink}
            onCancelOutgoing={handleCancelOutgoing}
            onSendInvite={handleSendInvite}
            onResend={handleResend}
            />
          </Box>
          <Box
            sx={{
              gridColumn: { md: "1" },
              gridRow: { md: "2" },
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => setShowResolved((value) => !value)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                width: "100%",
                flexShrink: 0,
                px: "12px",
                py: "6px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: FN.primary,
                  letterSpacing: "0.03px",
                  lineHeight: "16px",
                }}
              >
                {showResolved ? "Hide request history" : "Show request history"}
              </Typography>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  display: "flex",
                  transform: showResolved ? "rotate(180deg)" : "none",
                }}
              >
                <FacilityNetworkFigmaIcon src={chevronTeal} size={12} />
              </Box>
            </Box>
            {showResolved ? (
              <BucketPanel
                title="Request history"
                count={resolved.length}
                items={resolved}
                selectedKey={selectedKey}
                onSelect={(item) => {
                  setActionError(null);
                  setSelectedKey(item.key);
                }}
              />
            ) : null}
          </Box>
        </Box>
      )}
    </Box>
  );
}
